/**
 * Admin → Channel Mirrors
 *
 * Manages the Gateway Translation Mirror's EN↔ES channel pairs.
 * Add a pair → within 60s (or hit Reload) the bot starts translating
 * every message in real time in both directions.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import {
  Languages, Plus, Trash2, RefreshCw, Activity, ArrowLeftRight, CheckCircle2, XCircle, Link2,
} from "lucide-react";

const TESTIDS = {
  root: "admin-channel-mirrors-root",
  createBtn: "admin-channel-mirrors-create",
  reloadBtn: "admin-channel-mirrors-reload",
  pairRow: "admin-channel-mirrors-row",
  toggleBtn: "admin-channel-mirrors-toggle",
  deleteBtn: "admin-channel-mirrors-delete",
  dialog: "admin-channel-mirrors-dialog",
  saveBtn: "admin-channel-mirrors-save",
};

export default function AdminChannelMirrors() {
  const [pairs, setPairs] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [reloading, setReloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        api.get("/admin/channel-mirrors"),
        api.get("/admin/channel-mirrors/status"),
      ]);
      setPairs(pRes.data);
      setStatus(sRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      // Status-only refresh so the "connected" indicator stays fresh.
      api.get("/admin/channel-mirrors/status").then((r) => setStatus(r.data)).catch(() => {});
    }, 15_000);
    return () => clearInterval(id);
  }, []);

  const autoPair = async () => {
    try {
      const { data } = await api.post("/admin/channel-mirrors/auto-pair");
      if (!data.ok) return toast.error(data.error || "Auto-pair failed");
      toast.success(`Paired ${data.added.length} channel(s)`, { description: data.added.join(", ") || `All ${data.already_paired} already paired` });
      await api.post("/admin/channel-mirrors/reload").catch(() => {});
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Auto-pair failed");
    }
  };

  const reload = async () => {
    setReloading(true);
    try {
      await api.post("/admin/channel-mirrors/reload");
      toast.success("Gateway reloaded — mirrors now live");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reload failed");
    } finally {
      setReloading(false);
    }
  };

  const [diagnosing, setDiagnosing] = useState(false);
  const diagnose = async () => {
    // Resolves each pair's EN + ES channel against Discord REST so you
    // can see at a glance:
    //  - whether the channel ID is correct (`name` populated)
    //  - whether the bot has access (403 = kicked / missing View perms)
    //  - whether the pair is enabled
    // Logs the full response to the browser console; summary is in the toast.
    setDiagnosing(true);
    try {
      const r = await api.get("/admin/channel-mirrors/diagnose");
      // eslint-disable-next-line no-console
      console.log("[channel-mirrors diagnose]", r.data);
      const lines = r.data.map((p) => {
        const enLabel = p.en.ok ? `#${p.en.name}` : `❌ ${p.en.error || "unresolved"}`;
        const esLabel = p.es.ok ? `#${p.es.name}` : `❌ ${p.es.error || "unresolved"}`;
        const state = p.enabled ? "✓" : "⏸";
        return `${state} ${enLabel} ↔ ${esLabel}${p.name ? `  (${p.name})` : ""}`;
      });
      const brokenCount = r.data.filter((p) => !p.en.ok || !p.es.ok).length;
      toast[brokenCount > 0 ? "warning" : "success"](
        `${r.data.length} pair(s) — ${brokenCount} broken`,
        { description: lines.join("\n") || "No pairs configured yet.", duration: 15000 },
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Diagnose failed");
    } finally {
      setDiagnosing(false);
    }
  };

  const toggle = async (p) => {
    try {
      await api.post(`/admin/channel-mirrors/${p.id}/toggle`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Toggle failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(
      `Remove the ${p.name || "unnamed"} mirror pair?\n\nMessages already mirrored will stay in Discord — only future messages stop being translated.`,
    )) return;
    try {
      await api.delete(`/admin/channel-mirrors/${p.id}`);
      toast.success("Pair removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid={TESTIDS.root} className="space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Languages size={26} className="text-amber-600" />
            Channel Mirrors
          </h1>
          <p className="text-sm text-slate-500 max-w-xl mt-1">
            Real-time EN↔ES message translation across paired Discord channels.
            Every message in a mirrored channel is translated and re-posted in
            the paired channel with the original author's name and avatar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={diagnosing}
            onClick={diagnose}
            data-testid="admin-channel-mirrors-diagnose-btn"
          >
            <Activity size={14} className={`mr-1.5 ${diagnosing ? "animate-pulse" : ""}`} />
            Diagnose
          </Button>
          <Button
            data-testid={TESTIDS.reloadBtn}
            variant="outline"
            disabled={reloading}
            onClick={reload}
          >
            <RefreshCw size={14} className={`mr-1.5 ${reloading ? "animate-spin" : ""}`} />
            Reload gateway
          </Button>
          <Button data-testid="admin-channel-mirrors-auto-pair" variant="outline" onClick={autoPair}>
            <Link2 size={14} className="mr-1.5" />
            Auto-pair channels
          </Button>
          <Button
            data-testid={TESTIDS.createBtn}
            onClick={() => setCreating(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Plus size={14} className="mr-1.5" />
            New pair
          </Button>
        </div>
      </header>

      <StatusBar status={status} />

      {loading ? (
        <div className="text-slate-500 text-sm py-12 text-center">Loading…</div>
      ) : pairs.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-xl py-16 text-center">
          <ArrowLeftRight size={30} className="mx-auto text-slate-300 mb-3" />
          <div className="font-semibold text-slate-700">No mirror pairs yet</div>
          <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Add your first EN↔ES pair. The bot will start translating messages
            in both directions the moment you save (or hit Reload gateway).
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {pairs.map((p) => (
            <PairRow
              key={p.id}
              pair={p}
              onToggle={() => toggle(p)}
              onDelete={() => remove(p)}
            />
          ))}
        </div>
      )}

      {creating && (
        <PairEditor
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function StatusBar({ status }) {
  if (!status) return null;
  const connected = status.gateway_connected;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard
        label="Bot status"
        value={connected ? "Connected" : "Disconnected"}
        icon={connected
          ? <CheckCircle2 size={16} className="text-emerald-600" />
          : <XCircle size={16} className="text-rose-600" />}
        tone={connected ? "emerald" : "rose"}
      />
      <StatCard
        label="Latency"
        value={status.latency_ms ? `${status.latency_ms} ms` : "—"}
        icon={<Activity size={16} className="text-slate-500" />}
      />
      <StatCard
        label="Active pairs"
        value={`${status.active_pairs} / ${status.total_pairs}`}
        icon={<ArrowLeftRight size={16} className="text-amber-600" />}
      />
      <StatCard
        label="Mirrored messages"
        value={status.mirrored_messages.toLocaleString()}
        icon={<Languages size={16} className="text-slate-500" />}
      />
    </div>
  );
}

function StatCard({ label, value, icon, tone }) {
  const toneClass = tone === "emerald" ? "bg-emerald-50 border-emerald-200" :
                    tone === "rose"    ? "bg-rose-50 border-rose-200" :
                    "bg-white border-slate-200";
  return (
    <div className={`rounded-lg border p-3 ${toneClass}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
        {icon} {label}
      </div>
      <div className="mt-1 font-display font-bold text-lg text-slate-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function PairRow({ pair, onToggle, onDelete }) {
  return (
    <div
      data-testid={TESTIDS.pairRow}
      className={`bg-white border rounded-lg p-3 flex items-center gap-3 ${
        pair.enabled ? "border-emerald-200" : "border-slate-200 opacity-60"
      }`}
    >
      <Switch
        data-testid={TESTIDS.toggleBtn}
        checked={pair.enabled}
        onCheckedChange={onToggle}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900">{pair.name || "Unnamed pair"}</span>
          {pair.enabled && (
            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] uppercase tracking-widest">
              Live
            </Badge>
          )}
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs font-mono text-slate-500">
          <span>{pair.en_channel_id}</span>
          <ArrowLeftRight size={12} className="text-slate-400" />
          <span>{pair.es_channel_id}</span>
        </div>
      </div>
      <Button
        data-testid={TESTIDS.deleteBtn}
        variant="outline"
        size="sm"
        onClick={onDelete}
        className="text-rose-700 border-rose-200 hover:bg-rose-50 h-8"
      >
        <Trash2 size={12} />
      </Button>
    </div>
  );
}

function PairEditor({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    en_channel_id: "",
    es_channel_id: "",
    enabled: true,
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!form.en_channel_id.trim() || !form.es_channel_id.trim()) {
      toast.error("Both channel IDs are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/channel-mirrors", form);
      // Immediately reload the gateway so the pair goes live in seconds,
      // not on the 60s auto-reload tick.
      try { await api.post("/admin/channel-mirrors/reload"); } catch (_) {}
      toast.success(`Mirror ${form.name || "pair"} is live`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={TESTIDS.dialog} className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">New mirror pair</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Display name
            </label>
            <Input
              value={form.name}
              onChange={set("name")}
              placeholder="The Pit · EN↔ES"
              maxLength={80}
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Just for your admin list — not sent to Discord.
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              English channel ID
            </label>
            <Input
              value={form.en_channel_id}
              onChange={set("en_channel_id")}
              placeholder="1548058329190768760"
              className="font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Spanish channel ID
            </label>
            <Input
              value={form.es_channel_id}
              onChange={set("es_channel_id")}
              placeholder="1548123456789012345"
              className="font-mono"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Right-click each channel in Discord → Copy Channel ID.
              Requires Developer Mode enabled in User Settings → Advanced.
            </p>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 leading-relaxed">
            <div className="font-bold uppercase tracking-widest text-slate-500 mb-1">
              Bot permissions needed
            </div>
            The bot must have <b>View Channel</b>, <b>Send Messages</b>, and
            <b> Manage Webhooks</b> on BOTH channels. Without Manage Webhooks the
            mirror falls back to plain-bot posts (uglier).
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              data-testid={TESTIDS.saveBtn}
              onClick={save}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {saving ? "Saving…" : "Create + activate"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
